import React, { useContext, useEffect, useState } from "react";
import { get, isEmpty } from "lodash";
import { Form, Button } from "react-bootstrap";
import { Formik, Field } from "formik";
import {
  MultiSelectDropdown,
  SearchableList,
  FieldDropSide,
  WarningModal,
  LoaderWrapper,
} from "../../../components";
import {
  manageEligibilityFormNames,
  formFields,
  getFlowBasedFormValues,
  required,
  toMultiSelectValueById,
  requiredList,
} from "../../../utils";
import {
  manageEligibilityStore,
  setManagePageLevelData,
  setManagePlanToastInfo,
} from "../../../contexts";
import { useRouterParams, useRequest } from "../../../abstracts";
import {
  getCompaniesList,
  getPlanList,
  runVesting,
  getSourcesListForVesting,
} from "../../../services";

const initialValues = {};

const RunVestingProcessSlider = (props) => {
  const { onCancel, onSubmitted, setModalOpen } = props;
  const { state, dispatch } = useContext(manageEligibilityStore);
  const { flow } = useRouterParams();
  const formName = manageEligibilityFormNames.ELIGIBILITY_PROCESS;
  const [isInnerFormDirty, setIsInnerFormDirty] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const fields = formFields[formName];
  const [planList, setPlanList] = useState([]);
  const [sourceList, setSourceList] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { response: companies } = useRequest({
    method: getCompaniesList,
    payload: 1, //TenantId. HardCoding should be removed
    defaultResponse: [],
  });

  useEffect(() => {
    return () => setIsInnerFormDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (values) => {
    setIsLoading(true);
    runVesting({
      planId: values.planId,
      triggeredFrom: 4,
      ResonForTrigger: 3,
      sourceId: values.sources,
    })
      .then((response) => {
        setIsLoading(false);
        setModalOpen(false);
        if (response) {
          dispatch(
            setManagePlanToastInfo({
              showToast: true,
              toastMessage: "Scheduler run successfully",
            })
          );
          dispatch(
            setManagePageLevelData({
              formName: formName,
              fieldData: values,
            })
          );
          onSubmitted();
        }
      })
      .catch((error) => {
        //Handle Error
        setIsLoading(false);
        setModalOpen(false);
      });
    // setIsLoading(false);
  };

  const viewDirty = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          ...initialValues,
          ...getFlowBasedFormValues(get(state, formName, {}), flow),
        }}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          handleChange,
          setFieldValue,
          handleSubmit,
          values,
          dirty,
          ...rest
        }) => {
          const selectedSources = values[fields.sources];
          if (isInnerFormDirty !== dirty) {
            setIsInnerFormDirty(dirty);
          }

          const onCompanyChange = (value) => {
            setFieldValue(fields.companyName, companies[value].name);
            setFieldValue(fields.companyId, companies[value].id);
            setFieldValue(fields.planName, null);
            setFieldValue(fields.planId, null);
            setFieldValue(fields.sources, []);
            getPlanList(companies[value].id)
              .then((response) => {
                setPlanList(response);
              })
              .catch((error) => {
                //Handle Error
              });
          };

          const onPlanChange = (value) => {
            setFieldValue(fields.planName, planList[value].name);
            setFieldValue(fields.planId, planList[value].id);
            setFieldValue(fields.sources, []);
            getSourcesListForVesting(planList[value].id)
              .then((response) => {
                setSourceList(
                  response.map((source, index) => ({
                    label: source.name,
                    value: source.id,
                  }))
                );
                if (isEmpty(response))
                  setError("Selected Plan has immediate vesting rule");
                else setError("");
              })
              .catch((error) => {
                //Handle Error
              });
          };

          return (
            <LoaderWrapper isLoading={isLoading}>
              <Form
                autoComplete="off"
                className="mb-20"
                onSubmit={handleSubmit}
              >
                <Field
                  label="Company Name"
                  isRequired
                  name={fields.companyName}
                  value={values[fields.companyName]}
                  direction="bottom"
                  popupContent={
                    <SearchableList
                      label="Select Company"
                      options={
                        companies &&
                        companies.map((company, index) => ({
                          label: company.name,
                          value: index,
                        }))
                      }
                      onSelect={(value) => onCompanyChange(value)}
                      selectedValue={values[fields.companyName]}
                    />
                  }
                  validate={required}
                  component={FieldDropSide}
                />
                <Field
                  label="Plan name"
                  name={fields.planName}
                  value={values[fields.planName]}
                  direction="bottom"
                  isRequired
                  popupContent={
                    <SearchableList
                      label="Select Plan name"
                      options={
                        planList &&
                        planList.map((plan, index) => ({
                          label: plan.name,
                          value: index,
                        }))
                      }
                      onSelect={(value) => onPlanChange(value)}
                      selectedValue={values[fields.planName]}
                    />
                  }
                  validate={required}
                  component={FieldDropSide}
                />
                <Field
                  size="md"
                  label="Sources"
                  name={fields.sources}
                  isRequired
                  value={toMultiSelectValueById(selectedSources, sourceList)}
                  isMultiSelect
                  direction="bottom"
                  popupContent={
                    <MultiSelectDropdown
                      label="Select Sources"
                      options={sourceList}
                      onSelect={(value) => setFieldValue(fields.sources, value)}
                      value={values[fields.sources]}
                    />
                  }
                  validate={requiredList}
                  component={FieldDropSide}
                />
                <p className="error-message-deferral">{error}</p>

                <Button
                  variant="secondary"
                  onClick={dirty ? viewDirty : onCancel}
                  className="mr-3"
                >
                  Cancel
                </Button>
                <Button type="submit">Run Vesting</Button>
              </Form>
            </LoaderWrapper>
          );
        }}
      </Formik>
      <WarningModal show={modalVisible} onHide={closeModal}>
        <div className="remove-text">
          <h4>Are you sure?</h4>
          <p>Your changes may be lost. Do you want to continue ?</p>
          <Button className="remove-btn mr-4" onClick={onCancel}>
            Yes
          </Button>
          <Button className="cancel-btn" onClick={closeModal}>
            No
          </Button>
        </div>
      </WarningModal>
    </>
  );
};

export default RunVestingProcessSlider;
